import { useState, useEffect, useRef } from 'react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Loader2, Sparkles, FileText, Check, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import {
  QUIET_OVERFLOW_MENU_COLLISION_PADDING,
  QUIET_OVERFLOW_MENU_CONTENT_CLASS,
} from '@/lib/menu-chrome';
import { usePersistedState } from '@/hooks/use-persisted-state';

type GenerationMode = 'both' | 'title-only' | 'summary-only';

interface TitleEditModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  subtitle?: string;
  summary?: string;
  onSave: (title: string, summary?: string) => Promise<void>;
  onGenerateAI?: (mode: GenerationMode) => Promise<{ title?: string; summary?: string }>;
  showSummary?: boolean;
  placeholder?: string;
  summaryPlaceholder?: string;
}

export function TitleEditModal({
  open,
  onOpenChange,
  title: initialTitle,
  subtitle,
  summary: initialSummary = '',
  onSave,
  onGenerateAI,
  showSummary = false,
  placeholder = 'Enter title...',
  summaryPlaceholder = 'Enter summary...'
}: TitleEditModalProps) {
  const [title, setTitle] = useState(initialTitle);
  const [summary, setSummary] = useState(initialSummary);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Persist generation mode preference
  const [generationMode, setGenerationMode] = usePersistedState<GenerationMode>({
    key: 'flownotes_title_edit_generation_mode',
    defaultValue: 'both' as GenerationMode,
  });

  // Reset form when modal opens
  useEffect(() => {
    if (open) {
      setTitle(initialTitle);
      setSummary(initialSummary);
      // Focus input after a short delay
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [open, initialTitle, initialSummary]);

  const handleGenerate = async (mode: GenerationMode = generationMode) => {
    if (!onGenerateAI) return;

    setIsGenerating(true);
    try {
      const result = await onGenerateAI(mode);
      if (result.title && (mode === 'both' || mode === 'title-only')) {
        setTitle(result.title);
      }
      if (result.summary && (mode === 'both' || mode === 'summary-only')) {
        setSummary(result.summary);
      }

      const modeLabels = {
        'both': 'Title and summary',
        'title-only': 'Title',
        'summary-only': 'Summary'
      };
      toast.success(`${modeLabels[mode]} generated successfully`);
    } catch (error) {
      console.error('Failed to generate:', error);
      toast.error('Failed to generate. Please try again or enter manually.');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSave = async () => {
    if (!title.trim()) {
      toast.error('Title cannot be empty');
      return;
    }

    setIsSaving(true);
    try {
      await onSave(title.trim(), showSummary ? summary.trim() : undefined);
      toast.success('Title saved successfully');
      onOpenChange(false);
    } catch (error) {
      console.error('Failed to save:', error);
      toast.error('Failed to save title. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && e.metaKey) {
      e.preventDefault();
      handleSave();
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="p-0 gap-0 max-w-lg overflow-hidden top-[20vh] translate-y-0"
        showCloseButton={false}
        onKeyDown={handleKeyDown}
      >
        <DialogTitle className="sr-only">Edit Title</DialogTitle>
        <DialogDescription className="sr-only">
          Edit the title and optional summary
        </DialogDescription>

        {/* Header */}
        <div className="flex items-center border-b border-border px-3 py-2">
          <FileText className="h-4 w-4 text-muted-foreground mr-2 flex-shrink-0" />
          <Input
            ref={inputRef}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={placeholder}
            className="border-0 focus-visible:ring-0 focus-visible:ring-offset-0 h-10 flex-1 text-base font-medium"
            disabled={isGenerating || isSaving}
            maxLength={100}
          />
          {onGenerateAI && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={isGenerating || isSaving}
                  className="ml-2 flex-shrink-0"
                  title="Generate with AI"
                >
                  {isGenerating ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <>
                      <Sparkles className="h-4 w-4" />
                      <ChevronDown className="h-3 w-3 ml-0.5 opacity-50" />
                    </>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                className={QUIET_OVERFLOW_MENU_CONTENT_CLASS}
                collisionPadding={QUIET_OVERFLOW_MENU_COLLISION_PADDING}
              >
                <DropdownMenuItem
                  onClick={() => {
                    setGenerationMode('both');
                    handleGenerate('both');
                  }}
                  disabled={isGenerating}
                >
                  <div className="flex items-center justify-between w-full">
                    <span>Generate Both</span>
                    {generationMode === 'both' && <Check className="h-4 w-4" />}
                  </div>
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => {
                    setGenerationMode('title-only');
                    handleGenerate('title-only');
                  }}
                  disabled={isGenerating}
                >
                  <div className="flex items-center justify-between w-full">
                    <span>Title Only</span>
                    {generationMode === 'title-only' && <Check className="h-4 w-4" />}
                  </div>
                </DropdownMenuItem>
                {showSummary && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={() => {
                        setGenerationMode('summary-only');
                        handleGenerate('summary-only');
                      }}
                      disabled={isGenerating}
                    >
                      <div className="flex items-center justify-between w-full">
                        <span>Summary Only</span>
                        {generationMode === 'summary-only' && <Check className="h-4 w-4" />}
                      </div>
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>

        {/* Subtitle (filename, etc.) */}
        {subtitle && (
          <div className="px-3 py-1.5 text-xs text-muted-foreground border-b border-border/50 bg-muted/30">
            {subtitle}
          </div>
        )}

        {/* Summary field */}
        {showSummary && (
          <div className="px-3 py-3">
            <Textarea
              value={summary}
              onChange={(e) => setSummary(e.target.value)}
              placeholder={summaryPlaceholder}
              className="resize-none border-muted focus-visible:ring-1 focus-visible:ring-primary/50"
              rows={3}
              disabled={isGenerating || isSaving}
              maxLength={500}
            />
            <div className="flex items-center justify-between mt-1.5 text-xs text-muted-foreground">
              <span>Brief summary of the content</span>
              <span>{summary.length}/500</span>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="border-t border-border px-3 py-2 flex items-center justify-between bg-muted/30">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <kbd className={cn(
              "pointer-events-none inline-flex h-5 select-none items-center gap-1",
              "rounded border border-border bg-muted px-1.5 font-mono text-[10px]"
            )}>
              ⌘
            </kbd>
            <kbd className={cn(
              "pointer-events-none inline-flex h-5 select-none items-center gap-1",
              "rounded border border-border bg-muted px-1.5 font-mono text-[10px]"
            )}>
              ↵
            </kbd>
            <span>to save</span>
          </div>

          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="ghost"
              onClick={() => onOpenChange(false)}
              disabled={isSaving}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={handleSave}
              disabled={!title.trim() || isSaving}
            >
              {isSaving ? (
                <Loader2 className="h-4 w-4 animate-spin mr-1" />
              ) : null}
              Save
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
